/// <reference types="@types/google.maps" />
import type { PlaceDetailsResponseData } from "@googlemaps/google-maps-services-js";
import {
  Client as GoogleMapsClient,
  Language,
  PlaceInputType,
  AddressType as GoogleMapsAddressType,
} from "@googlemaps/google-maps-services-js";
import type { Database } from "../types/supabase.gen";
import { convertCoordinatesToWkt } from "../utils/geography";

export const getGoogleMapsPlaceId = async function (textInput: string, params: { googleMapsKey: string }) {
  const googlemaps = new GoogleMapsClient();

  const { googleMapsKey } = params;

  const placeFromText = await googlemaps.findPlaceFromText({
    params: {
      key: googleMapsKey,
      input: textInput,
      inputtype: PlaceInputType.textQuery,
      language: Language.fr,
    },
  });

  const firstResult = placeFromText.data.candidates[0];

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!firstResult?.place_id) {
    // eslint-disable-next-line no-console
    console.log(`No place ID found for input: ${textInput}`);
    return;
  }

  return firstResult.place_id;
};

export const getGoogleMapsAddress = async function (textInput: string, params: { googleMapsKey: string }) {
  const googlemaps = new GoogleMapsClient();
  const { googleMapsKey } = params;

  const placeId = await getGoogleMapsPlaceId(textInput, params);

  if (!placeId) {
    // eslint-disable-next-line no-console
    console.log(`No place ID found for input: ${textInput}`);
    return;
  }

  const placeDetails = await googlemaps.placeDetails({
    params: {
      key: googleMapsKey,
      place_id: placeId,
      language: Language.fr,
      fields: ["formatted_address", "geometry/location", "address_components"],
    },
  });

  const placeResult = placeDetails.data.result;

  return placeResult;
};

export const convertToSupabaseAddress = function (googleMapsPlace: PlaceDetailsResponseData["result"]) {
  let street1 = "";
  let street2 = "";
  let postalCode = "";
  let country = "";
  let city = "";
  let department = "";
  let region = "";

  const addressComponents = googleMapsPlace.address_components ?? [];

  // Get each component of the address from the place details,
  // and then fill-in the corresponding field on the form.
  // place.address_components are google.maps.GeocoderAddressComponent objects
  // which are documented at http://goo.gle/3l5i5Mr
  for (const component of addressComponents) {
    if (
      component.types.includes(GoogleMapsAddressType.street_number) ||
      component.types.includes(GoogleMapsAddressType.route)
    ) {
      if (!street1) street1 = component.long_name;
      else street2 = component.long_name;
    } else if (component.types.includes(GoogleMapsAddressType.postal_code)) {
      postalCode = component.long_name;
    } else if (component.types.includes(GoogleMapsAddressType.postal_code_suffix)) {
      postalCode = `${postalCode}-${component.long_name}`;
    } else if (component.types.includes(GoogleMapsAddressType.locality)) {
      city = component.long_name;
    } else if (component.types.includes(GoogleMapsAddressType.administrative_area_level_1)) {
      region = component.long_name; // Extract the region (e.g., "Île-de-France")
    } else if (component.types.includes(GoogleMapsAddressType.administrative_area_level_2)) {
      department = component.long_name; // Extract the department (e.g., "Paris")
    } else if (component.types.includes(GoogleMapsAddressType.country)) {
      country = component.long_name;
    }
  }

  const { geometry } = googleMapsPlace;

  const longitude = geometry?.location.lng ?? 0;
  const latitude = geometry?.location.lat ?? 0;
  const coordinates = { lng: longitude, lat: latitude };

  const formattedAddress = googleMapsPlace.formatted_address ?? "";

  const address: Database["public"]["CompositeTypes"]["address"] = {
    formatted: formattedAddress,
    street1,
    street2,
    city,
    postal_code: postalCode,
    region,
    department,
    country,
    coordinates,
  };

  return address;
};

export const convertToSupabaseLocation = function (googleMapsPlace: PlaceDetailsResponseData["result"]) {
  const { geometry } = googleMapsPlace;

  const coordinates = { lng: geometry?.location.lng ?? 0, lat: geometry?.location.lat ?? 0 };

  return convertCoordinatesToWkt(coordinates);
};

type GoogleMapsPlace = PlaceDetailsResponseData["result"] | google.maps.places.PlaceResult;

export const convertToAddressTableInsert = function (
  googleMapsPlace: GoogleMapsPlace
): Database["public"]["Tables"]["address_table"]["Insert"] {
  let street1 = "";
  let street2 = "";
  let postalCode = "";
  let country = "";
  let city = "";
  let department = "";
  let region = "";

  const addressComponents = googleMapsPlace.address_components ?? [];

  // Get each component of the address from the place details,
  // and then fill-in the corresponding field on the form.
  // place.address_components are google.maps.GeocoderAddressComponent objects
  // which are documented at http://goo.gle/3l5i5Mr
  for (const component of addressComponents) {
    if (component.types.includes("street_number") || component.types.includes("route")) {
      if (!street1) street1 = component.long_name;
      else street2 = component.long_name;
    } else if (component.types.includes("postal_code")) {
      postalCode = component.long_name;
    } else if (component.types.includes("postal_code_suffix")) {
      postalCode = `${postalCode}-${component.long_name}`;
    } else if (component.types.includes("locality")) {
      city = component.long_name;
    } else if (component.types.includes("administrative_area_level_1")) {
      region = component.long_name; // Extract the region (e.g., "Île-de-France")
    } else if (component.types.includes("administrative_area_level_2")) {
      department = component.long_name; // Extract the department (e.g., "Paris")
    } else if (component.types.includes("country")) {
      country = component.long_name;
    }
  }

  const { geometry } = googleMapsPlace;

  const isFunction = function (value: unknown): value is () => number {
    return typeof value === "function";
  };

  const getCoordinate = function (coord: number | (() => number) | undefined) {
    return isFunction(coord) ? coord() : coord;
  };

  const longitude = getCoordinate(geometry?.location?.lng);
  const latitude = getCoordinate(geometry?.location?.lat);

  const formattedAddress = googleMapsPlace.formatted_address;

  const address: Database["public"]["Tables"]["address_table"]["Insert"] = {
    formatted: formattedAddress,
    street1,
    street2,
    city,
    postal_code: postalCode,
    department_name: department,
    region_name: region,
    country,
    latitude,
    longitude,
  };

  return address;
};
